import { CurrencyCode } from '@percent/utility'
import { AxiosInstance } from 'axios'
import { Alpha3Code } from 'i18n-iso-countries'

export type grantsServiceCapabilities = {
  percentClient: AxiosInstance
}

export type ActivitySubtag = {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string | null
  tag: {
    id: string
    name: string
    description: string | null
    createdAt: string
    updatedAt: null
  }
}

export type EligibilityResult = {
  eligibilityStatus: 'pass' | 'fail' | null
  confirmedActivitySubTags: ActivitySubtag[] | null
  rejectedActivitySubTags: ActivitySubtag[] | null
}

export type Organisation = {
  id: string | null
  name: string
  website: string | null
  orgEmail: string | null
  countryCode: Alpha3Code
  address: string | null
  registryName: string | null
  organisationIdInRegistry: string | null
  logo: string | null
  eligibilityResult: EligibilityResult | null
}

export type GetCandidatesParams = {
  locale?: string
}

export type GetCandidateDetailsParams = {
  candidateId: string
  locale: string
}

export type Nominator = {
  name: string
  email: string
}

export type Nomination = {
  id: string
  nominator?: Nominator
  group?: string | null
  date: string
}

export type PaymentStatus = 'processing' | 'paid'
export type CandidateDetails = {
  id: string
  currentState: string
  program: {
    id: string
    name: string
    description: string
    partnerName: string
  }
  organisation: Organisation
  award?: {
    currency: CurrencyCode
    amount: number
  }
  paymentDetails?: {
    disbursedBy: string
    payment?: {
      amount: number
      currency: CurrencyCode
    }
    status: PaymentStatus
    paidAt?: string
  }
  nominations?: Nomination[]
}

export type TaskStatus = 'todo' | 'done' | 'cancelled'

export type TaskAssignment = {
  assignedTo?: string
  assignedBy?: string
  assignedAt?: Date
}

export enum TaskType {
  BANK_DETAILS = 'bank_details',
  REVIEW = 'review'
}

export type NonprofitTask = {
  id: string
  status: TaskStatus
  createdAt: string
  completedAt: string | null
  dueByDate: string | null
  order: number | null
  name: string
  type: TaskType
  form: {
    formId?: string
    displayName?: string
  } | null
  assignment: TaskAssignment
}

export type CandidateTask = Pick<NonprofitTask, 'id' | 'status' | 'name' | 'dueByDate'> & {
  assignment?: TaskAssignment
}

export type Candidate = {
  id: string
  program: {
    id: string
    name: string
    description: string
    budgetMin: number | null
    budgetMax: number | null
    currency: string | null
    partner: {
      id: string
      logo: string | null
    }
  }
  tasks: CandidateTask[]
  nominatorGroups: string[]
}

type BaseCandidateFormQuestion = {
  id: string
  key: string
  label: string
  placeholder?: string
  description?: string
  instructions?: string
  required: boolean
  hidden?: boolean
  order: number
  validationRules?: import('@percent/utility').ValidationRule[]
}

export type CandidateFormSelectOption = {
  label: string
  value: string
  conditionalQuestionIds?: string[]
}

export type CandidateFormDecisionKey = 'pass' | 'fail'

export type CandidateFormDecisionOption = CandidateFormSelectOption & {
  key: CandidateFormDecisionKey
}

export type ScoreScale = {
  maxScore: number
  scoreWeight?: number
}

export type MoneySubType = 'award' | 'final_award' | 'operating_budget'

export type CandidateFormQuestion =
  | (BaseCandidateFormQuestion & {
      type: 'text' | 'documents' | 'website' | 'email' | 'country_search' | 'date' | 'scheduled_event' | 'number'
    })
  | (BaseCandidateFormQuestion & { type: 'textarea'; maxLength?: number })
  | (BaseCandidateFormQuestion & ScoreScale & { type: 'score' })
  | (BaseCandidateFormQuestion & { type: 'select'; options: CandidateFormSelectOption[]; multiple?: boolean })
  | (BaseCandidateFormQuestion & { type: 'decision'; options: CandidateFormDecisionOption[] })
  // `money` questions are paired by key prefix: `<prefix>.amount` + `<prefix>.currency`. The amount is
  // captured in minor units. Bounds on the amount half ride on the shared validationRules.
  | (BaseCandidateFormQuestion & {
      type: 'money'
      subType?: MoneySubType
      defaultCurrency?: CurrencyCode
      allowedCurrencies?: CurrencyCode[]
    })

export type CandidateFormPage = {
  id: string
  title: string
  description?: string
  order: number
  questions: CandidateFormQuestion[]
}

export type CandidateForm = {
  candidateId: string
  candidateForm: {
    id: string
    type: string
    displayName: string
    title: string
    deadline: string | null
    description: string
    pages: CandidateFormPage[]
  }
}

export type CandidateDocument = {
  id: string
  name: string
  url: string
  createdAt: string
}

export type UploadCandidateDocumentsParams = {
  candidateId: string
  formData: FormData
}

export type GetMyTasksParams = {
  pageSize?: number
  programId?: string
  candidateId?: string
  withForm?: boolean
  status?: TaskStatus
}

export type GetCandidateFormParams = {
  programId: string
  candidateId: string
  formId: string
  locale: string
}

export type PostCandidateFormParams = {
  programId: string
  candidateId: string
  taskId: string
  formAnswers: { questionId: string; answer: string | string[] }[]
}

export type UploadCandidateDocumentsResponse = {
  s3Keys: string[]
}

export type PostCandidateBankDetailsParams = {
  candidateId: string
  taskId: string
  programId: string
  payload: {
    formData: FormData
  }
}

export type TranslatedAnswer = {
  detectedSourceLanguage: string
  localizedAnswers: Record<string, string>
}

export type FormSubmissionAnswer = {
  pageId?: string
  pageTitle?: string
  id: string
  questionId: string
  questionLabel: string
  dashboardLabel?: string
  answer: string
  translatedAnswer?: TranslatedAnswer | null
  type: string
  decision?: string | null
}

export type TaskSubmission = {
  id: string
  taskId: string
  displayName: string
  author: {
    accountId: string
    fullName?: string | null
    email: string
  }
  formAnswers: FormSubmissionAnswer[]
  audience?: string
  createdAt: string
  scoreAggregate?: {
    mean: number
    sum: number
  }
}

export type GetFormSubmissionsParams = {
  programId: string
  candidateId: string
}

export type UpdateTaskAssigneeParams = {
  taskId: string
  assignedTo: string
}
